// @ts-check
/**
 * src/api/azureClient.js
 *
 * Azure Functions API client for the SeaSuite frontend.
 *
 * All database access goes through Azure Functions HTTP endpoints.
 *
 * Required env var:
 *   VITE_AZURE_FUNCTIONS_URL — e.g. https://seasuite-func-prod-dfdmfmaxeccbawck.denmarkeast-01.azurewebsites.net
 */

import { fetchThroughRestart } from './serviceRestart.js';
import { reportError } from '../utils/errorReporter.js';
import {
  writesTenantState,
  claimTenantStateVersion,
  adoptTenantStateVersion,
} from './tenantStateVersion.js';
import { getSessionToken, setSessionToken, hasSessionToken } from './sessionToken.js';
import { getStepUpToken, requestStepUp, isStepUpRequired, STEP_UP_CANCELLED } from './stepUp.js';

export { getSessionToken, setSessionToken, hasSessionToken };

/**
 * An Error carrying the HTTP status that produced it. Thrown by azureGet /
 * azurePost for any non-OK response so callers can branch on the status
 * (e.g. 409 write-conflict) instead of string-matching the message.
 *
 * `serverState` / `serverStateVersion` are present only on a state-write 409
 * that carried the winning snapshot with it (GH #5358).
 *
 * @typedef {Error & { httpStatus?: number, conflictReason?: string, conflicts?: Array<object>, correlationId?: string, serverDoc?: unknown, serverState?: unknown, serverStateVersion?: string|null, responseBody?: Record<string, unknown>, validationErrors?: Array<{ code: string, path: string, message: string, stepId?: string }> }} ApiError
 */

/**
 * Query-string values accepted by the request helpers. `null` and `undefined`
 * entries are dropped rather than serialised as the strings "null"/"undefined".
 *
 * @typedef {Record<string, string | number | boolean | null | undefined>} QueryParams
 */

/**
 * One of the three deployed Function Apps.
 * @typedef {'production' | 'test' | 'development'} AzureEnvironment
 */

const AZURE_FUNCTIONS_URL = import.meta.env.VITE_AZURE_FUNCTIONS_URL;

if (!AZURE_FUNCTIONS_URL && import.meta.env.VITE_BACKEND === 'azure') {
  throw new Error(
    '[SeaSuite] Missing Azure env var.\n' +
      'Set VITE_AZURE_FUNCTIONS_URL in your .env file.\n' +
      'See .env.azure.example for details.'
  );
}

// ── Central error surfacing ─────────────────────────────────────────────────
//
// Every azureGet/azurePost/envFetch failure is reported in the background via
// errorReporter (for diagnostics) and broadcast as a 'seasuite:api-error'
// window event so a single app-shell banner (ApiErrorBanner) can tell the
// user something failed — without every one of the ~30 api/*.js callers
// needing its own error UI. This fires BEFORE the error reaches a caller's
// own catch block, so it still surfaces even when that catch block only logs
// and swallows the failure.
// Conflict reasons that mean a real person changed the record first. These are
// the expected outcome of optimistic concurrency, not defects: the user's own
// recovery UI handles them and no bug should be filed. Every OTHER reason —
// seed-race, sql-deadlock, sql-lock-timeout, sql-duplicate-key, and `unknown`
// from a backend older than the diagnostics — is server-side contention the
// user did not cause, and is reported. `unknown` is deliberately on the
// reported side: failing towards capture is what keeps a new or untagged
// conflict path from going silent the way this whole class already did.
// 'governance-role-unheld' (FDNA-01): risks-write refusing a step nobody can
// perform because no one holds the role it is gated on. A tenant
// configuration gap with its own task and message, not a defect to file.
const EXPECTED_CONFLICT_REASONS = new Set([
  'stale-version',
  'row-vanished',
  // CONC-01: two people changed the same field; the conflict notice owns it.
  'field-conflict',
  'governance-role-unheld',
  // RPDA-05: release-flag-write refusing a switch-off that would strand records
  // (RFLG-B-006) — a deliberate refusal the Releases tab explains, not a defect.
  'release-flag-has-records',
  'release-flag-tenants-have-records',
  // PDW-HALT-2: workflow-halt-reviews-write — two people deciding the same
  // review (§13 d, the review screen offers "replace it with my decision"),
  // and the deliberate refusals that screen explains (a landed Retire stands;
  // a later review exists; the review closed; nothing to supersede; the
  // workflow is already retired).
  'decision-conflict',
  // Slice 4b: the platform answered (or the request was withdrawn) while a
  // person was deciding — the review screen names both values.
  'resume-request-conflict',
  // The super-admin Decline refused because the tenant's flag already reads ON.
  'flag-on',
  'retired',
  'later-review',
  'review-closed',
  'nothing-to-supersede',
  'definition-retired',
]);

// Fetch spec §keepalive: the total size of all pending keepalive requests in
// a browsing context is capped at 64 KiB. A single keepalive request whose
// body alone would exceed the remaining budget fails immediately with no
// network attempt. We only ever issue one keepalive write at a time here, so
// budgeting against the full 64 KiB (rather than some fraction of it) is
// correct; leave a small margin for headers/overhead the browser also counts.
const KEEPALIVE_BODY_BUDGET_BYTES = 63 * 1024;

/**
 * §16 step-up (./stepUp.js): attach the in-memory step-up token when one is
 * held. Every gated endpoint reads it from this header; ungated ones ignore it.
 *
 * @param {Record<string, string>} headers
 * @returns {Record<string, string>}
 */
function withStepUpHeader(headers) {
  const stepUp = getStepUpToken();
  if (stepUp) headers['X-Step-Up-Token'] = stepUp;
  return headers;
}

/**
 * A gated endpoint answered `403 { stepUp: 'required' }`: open the re-verify
 * dialog and, once the user has re-verified, send the request once more.
 * Throws when the user cancels — the operation did not happen and the caller
 * must not treat it as done. A second refusal falls through to the caller's
 * ordinary 403 handling.
 *
 * @param {Response} res
 * @param {() => Promise<Response>} retry
 * @returns {Promise<Response>}
 */
async function retryAfterStepUp(res, retry) {
  if (!(await isStepUpRequired(res))) return res;
  if (!(await requestStepUp())) {
    /** @type {ApiError & { stepUpCancelled?: boolean }} */
    const cancelled = new Error(STEP_UP_CANCELLED);
    cancelled.httpStatus = 403;
    cancelled.stepUpCancelled = true;
    throw cancelled;
  }
  return retry();
}

/**
 * @param {unknown} error
 * @param {{ path?: string, method?: string, silent?: boolean }} [context]
 * @returns {void}
 */
function surfaceApiError(error, { path, method, silent } = {}) {
  // Read message/stack off anything object-shaped, not just real Errors — some
  // callers reject with a plain `{ message }` and narrowing to `instanceof
  // Error` would silently downgrade those to "[object Object]".
  const err = /** @type {ApiError} */ (error && typeof error === 'object' ? error : {});
  const message = err.message || String(error);
  // 'Session expired' is already told to the user via the dedicated
  // seasuite:session-expired logout flow — don't also show a generic banner.
  if (message === 'Session expired') return;
  // The user dismissed the step-up dialog themselves — not a failure to report.
  if (message === STEP_UP_CANCELLED) return;
  // `silent: true` is an explicit opt-out for background, best-effort calls
  // whose own caller already swallows the failure with its own .catch() and
  // never shows the user anything (e.g. useEntityPresence's heartbeat/poll/
  // leave pings, sent every few seconds regardless of user action) — never
  // for a call whose failure carries real, unsaved user content. Reporting
  // these anyway filed a GitHub issue and popped the banner for a
  // never-user-visible background ping (2026-08-18 auto-reported triage).
  if (silent) return;
  if (err.httpStatus === 409) {
    // A 409 is not one thing, and treating it as one is why it went unreported
    // for so long.
    //
    // For a GENUINE concurrent edit — someone really did change the record
    // underneath this user — the 409 is optimistic concurrency working, and
    // every call site already shows purpose-built recovery UI
    // (DocumentConflictModal, SaveConflictBanner, …). Filing a bug for it was
    // pure noise, which is what #5484 correctly stopped.
    //
    // But the same status code is also returned for contention the user
    // neither caused nor can resolve: two requests racing to create the same
    // row, a MERGE deadlock, a lock timeout. Those are defects. #5484 could
    // not tell them apart, so it silenced both — and a customer hitting the
    // second kind produced no banner, no report, and no record anywhere. The
    // server now names the kind (shared/writeConflictDiag.js), so we can
    // report the defects and stay quiet about the rest.
    //
    // Still no banner either way: the caller's own recovery UI owns what the
    // user sees. This is background capture only.
    const reason = err.conflictReason || 'unknown';
    console.warn(`[conflict] 409 ${method || ''} ${path || ''} — reason: ${reason}`);
    if (!EXPECTED_CONFLICT_REASONS.has(reason)) {
      const endpoint = `${method || ''} ${(path || '').split('&')[0]}`.trim();
      reportError({
        message: `Write refused as a conflict (${reason}) on ${endpoint}`,
        stack: err.stack,
        context: `conflict:${reason} ${endpoint}`,
        // One issue per (endpoint, reason). The generic message is identical
        // for every conflict in the product, and the minified stack varies by
        // call path, so neither alone groups these correctly.
        dedupKey: `conflict|${reason}|${endpoint}`,
      });
    }
    return;
  }
  // A server failure the backend already captured is NOT reported again from
  // here. shared/errorCapture.js files it with the function name, action,
  // tenant, user, SQL error number and stack; a second issue built from a
  // generic "Internal server error" string would be a strictly worse duplicate
  // of it. The id is surfaced instead, so the client-side trail and the server
  // log line name the same invocation.
  if (err.correlationId) {
    console.warn(
      `[api] ${method || ''} ${path || ''} failed — correlationId: ${err.correlationId}`
    );
  } else {
    // No correlation id: a transport failure that never reached a function, or
    // a backend older than the capture layer. Either way nothing captured it
    // server-side, so this is the only chance to record it.
    //
    // This branch used to run for EVERY server 5xx, because the id never
    // reached the browser at all — `jsonResponse` stringifies the body before
    // errorCapture runs, and errorCapture only extended an object (GH #7673).
    // So the sentence above was false for the whole `jsonResponse` family, and
    // the branch above it — the one that exists to prevent "a strictly worse
    // duplicate" — was unreachable. Both are now true.
    //
    // What the id means, precisely, so this stays honest: the server LOGGED
    // this failure in full, and filed an issue for it unless its own filing
    // rate limit had been reached — in which case that window files one
    // summary issue naming the suppressed count (errorCapture's
    // shouldFileWindowSummary). It never means "no record exists".
    reportError({
      message,
      stack: err.stack,
      context: `api:${method || ''} ${path || ''}`.trim(),
    });
  }
  window.dispatchEvent(new CustomEvent('seasuite:api-error', { detail: { message } }));
}

// ── Session state ──────────────────────────────────────────────────────────
// The token itself lives in ./sessionToken.js (imported and re-exported
// above) — see that file's header for why. Everything else session-related
// (tenant id) stays here.

/** @type {string | null} */
let _tenantId = null;

/**
 * @param {string | null | undefined} id
 * @returns {void}
 */
export function setTenantId(id) {
  _tenantId = id || null;
}

/** @returns {string | null} */
export function getTenantId() {
  return _tenantId;
}

// ── Session refresh ───────────────────────────────────────────────────────

/**
 * Calls session-refresh to re-issue the JWT with the user's current DB role.
 * Used to silently recover from stale-role 403s without showing an error.
 *
 * Returns { ok, networkError }:
 *   ok: true              — refreshed; retry the original request.
 *   ok: false, networkError: true  — the refresh call itself failed to reach
 *     the server (offline / transient blip). This is NOT proof the role is
 *     still stale — callers must not present it as a permission denial (F-55).
 *   ok: false, networkError: false — the server responded but declined to
 *     issue a new token (still a genuine forbidden / no-op refresh).
 *
 * @returns {Promise<{ ok: boolean, networkError: boolean }>}
 */
async function refreshSessionToken() {
  if (!getSessionToken()) return { ok: false, networkError: false };
  try {
    const res = await fetch(`${AZURE_FUNCTIONS_URL}/api/session-refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getSessionToken()}`,
      },
    });
    if (!res.ok) return { ok: false, networkError: false };
    const { token } = await res.json();
    if (token) {
      setSessionToken(token);
      return { ok: true, networkError: false };
    }
    return { ok: false, networkError: false };
  } catch {
    return { ok: false, networkError: true };
  }
}

// ── Session expiry ────────────────────────────────────────────────────────

/**
 * Fires a custom 'seasuite:session-expired' event when a 401 is returned.
 * useAuth listens for this and triggers logout + redirect to login.
 *
 * @returns {void}
 */
export function handleSessionExpired() {
  // Only fire if we thought we had a session.
  //
  // Dispatch the event BEFORE clearing the token so synchronous listeners
  // (e.g. the diagnostics client flushing a `session.expired` event) can
  // read the still-valid token when they build their outgoing request.
  // useAuth listens to the same event and tears down the session — the
  // small reorder does not affect that path.
  if (getSessionToken()) {
    window.dispatchEvent(new CustomEvent('seasuite:session-expired'));
    setSessionToken(null);
  }
}

// ── HTTP helpers ───────────────────────────────────────────────────────────

/**
 * Make an authenticated request to an Azure Function.
 *
 * Unlike azureGet/azurePost this returns the raw Response and does not parse
 * or throw on non-OK statuses — the caller owns both.
 *
 * `headers` is narrowed to a plain string map (not the full `HeadersInit`)
 * because this helper spreads it into an object literal — a `Headers` instance
 * or an array of pairs would silently lose every header.
 *
 * @param {string} path — Function path (e.g. '/api/state-read')
 * @param {Omit<RequestInit, 'headers' | 'body'> & {
 *   headers?: Record<string, string>,
 *   body?: BodyInit | object | null,
 *   silent?: boolean
 * }} [options] - fetch options. `silent: true` opts a background/best-effort
 *   call (never shown to the user, already caught by its own caller) out of
 *   the generic error banner and auto-report — see surfaceApiError.
 * @returns {Promise<Response>}
 */
export async function azureFetch(path, options = {}) {
  const { silent, ...fetchOptions } = options;
  const url = `${AZURE_FUNCTIONS_URL}${path}`;

  /** @type {Record<string, string>} */
  const headers = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers || {}),
  };

  // Attach JWT if available
  if (getSessionToken()) {
    headers['Authorization'] = `Bearer ${getSessionToken()}`;
  }

  // Several functions commit the shared `tenant_state` row as a side effect of
  // an ordinary user action, which moves the ROWVERSION the debounced save is
  // holding. Hold that save off for the duration of the call and adopt the
  // version the response carries — recognised here rather than asked of each
  // call site, because the call sites that caused this (the assign and share
  // modals) had no way to know they needed to. See tenantStateVersion.js.
  const bumpsStateVersion = writesTenantState(path);
  const releaseStateVersion = bumpsStateVersion ? claimTenantStateVersion() : null;

  let response;
  try {
    const method = String(fetchOptions.method || 'GET').toUpperCase();
    const send = () =>
      fetchThroughRestart(
        () =>
          fetch(url, {
            ...fetchOptions,
            headers: withStepUpHeader({ ...headers }),
            body: fetchOptions.body
              ? typeof fetchOptions.body === 'string'
                ? fetchOptions.body
                : JSON.stringify(fetchOptions.body)
              : undefined,
          }),
        { write: method !== 'GET' && method !== 'HEAD' }
      );
    response = await retryAfterStepUp(await send(), send);
  } catch (error) {
    if (releaseStateVersion) releaseStateVersion();
    surfaceApiError(error, { path, method: options.method || 'GET', silent });
    throw error;
  }

  if (response.status === 401 && getSessionToken()) {
    handleSessionExpired();
  }

  if (bumpsStateVersion) {
    // Read from a clone, so the caller still receives an unconsumed body.
    // Adopt BEFORE releasing: reading the clone yields to the event loop, and
    // releasing first would reopen — for exactly that gap — the window this
    // claim exists to close.
    try {
      if (response.ok) adoptTenantStateVersion(await response.clone().json());
    } catch {
      /* not JSON, or unreadable — nothing to adopt */
    } finally {
      if (releaseStateVersion) releaseStateVersion();
    }
  }

  return response;
}

/**
 * GET request to an Azure Function. Returns parsed JSON.
 *
 * Throws an {@link ApiError} carrying `httpStatus` for any non-OK response,
 * after transparently retrying once behind a session refresh on a stale-role
 * 403 ('Forbidden: admin role required').
 *
 * @param {string} path
 * @param {QueryParams} [queryParams] - null/undefined entries are omitted
 * @param {{ signal?: AbortSignal, silent?: boolean }} [options] - `silent: true`
 *   opts a background/best-effort call out of the generic error banner and
 *   auto-report — see surfaceApiError.
 * @returns {Promise<any>} the parsed JSON body
 */
export async function azureGet(path, queryParams = {}, options = {}) {
  try {
    const url = new URL(`${AZURE_FUNCTIONS_URL}${path}`);
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }

    const doGet = async () => {
      /** @type {Record<string, string>} */
      const headers = { 'Content-Type': 'application/json' };
      if (getSessionToken()) headers['Authorization'] = `Bearer ${getSessionToken()}`;
      withStepUpHeader(headers);
      return fetchThroughRestart(() =>
        fetch(url.toString(), {
          headers,
          ...(options.signal ? { signal: options.signal } : {}),
        })
      );
    };

    let res = await retryAfterStepUp(await doGet(), doGet);
    if (res.status === 401 && getSessionToken()) {
      // #5876/#5877/#5878: this used to fall through to the generic
      // `!res.ok` handling below, which built and threw a fresh
      // "Azure Function returned 401" error — a different message from the
      // literal 'Session expired' the 403-refresh-retry path below (and
      // envFetch) already throw on the same condition. surfaceApiError's
      // session-expiry skip matches on that exact string, so a direct 401
      // never hit it: every parallel in-flight call at session expiry (a
      // stale JWT on page load is the common case) got auto-reported and
      // popped the generic error banner, right as the user was being logged
      // out anyway. Throwing the same sentinel here makes a direct 401
      // behave exactly like the already-correct retry-path and envFetch
      // cases — logout still fires via handleSessionExpired() either way.
      //
      // httpStatus is still set to 401 on this error (unlike the retry-path's
      // plain throw below) because usePersistence.js's save-failure handler
      // keys off `e?.httpStatus === 401` specifically to stop retrying a
      // doomed write once the session is gone — losing that would turn a
      // terminal auth failure back into a spinning retry loop.
      handleSessionExpired();
      /** @type {ApiError} */
      const sessionExpiredError = new Error('Session expired');
      sessionExpiredError.httpStatus = 401;
      throw sessionExpiredError;
    }
    if (res.status === 403) {
      // A permission or plan denial carries its status like every other non-OK
      // answer, so callers can tell a 403 from an outage.
      const forbidden = (message, responseBody) => {
        /** @type {ApiError} */
        const error = new Error(message || 'Azure Function returned 403');
        error.httpStatus = 403;
        error.responseBody = responseBody;
        return error;
      };
      const body = await res.json().catch(() => ({}));
      if (body.error === 'Forbidden: admin role required') {
        const refresh = await refreshSessionToken();
        if (refresh.ok) {
          res = await doGet();
          if (res.status === 401) {
            handleSessionExpired();
            throw new Error('Session expired');
          }
          if (res.status === 403) {
            // F-55: still forbidden after a successful refresh — a genuine
            // permission denial, not a stale role. Surface the retried
            // response's message (falling back to the original) instead of
            // letting it fall through to a generic "returned 403".
            const retryBody = await res.json().catch(() => ({}));
            throw forbidden(retryBody.error || body.error, retryBody);
          }
        } else if (refresh.networkError) {
          // The refresh request itself failed to reach the server — this is a
          // transient network blip, not evidence the role is still stale. Don't
          // report it as a permission denial (F-55).
          throw new Error(
            'Could not verify permissions — network error during session refresh. Please try again.'
          );
        } else {
          throw forbidden(body.error, body);
        }
      } else {
        throw forbidden(body.error, body);
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      /** @type {ApiError} */
      const error = new Error(err.message || err.error || `Azure Function returned ${res.status}`);
      error.httpStatus = res.status;
      // A 409 body now carries a `reason` naming WHY the write was refused
      // (stale-version / seed-race / sql-deadlock / …) — see
      // azure-functions/shared/writeConflictDiag.js. Absent against a backend
      // deployed before that change, so every consumer must treat it as
      // optional.
      if (typeof err.reason === 'string') error.conflictReason = err.reason;
      // A 422 that refuses a payload against a structured rule set (a workflow
      // publish, PDWB-01) lists the rules it broke; carried as-is so the screen
      // can show the server's own reasons. Absent on every other response.
      if (Array.isArray(err.errors)) error.validationErrors = err.errors;
      // Set by azure-functions/shared/errorCapture.js on every 5xx. Its presence
      // means the SERVER already captured this failure — structured log line,
      // correlation id, deduplicated GitHub issue, with far more context than
      // the browser has. See surfaceApiError for why that changes what we do.
      if (typeof err.correlationId === 'string') error.correlationId = err.correlationId;
      throw error;
    }
    return await res.json();
  } catch (error) {
    surfaceApiError(error, { path, method: 'GET', silent: options.silent });
    throw error;
  }
}

/**
 * POST request to an Azure Function. Returns parsed JSON.
 *
 * Throws an {@link ApiError} carrying `httpStatus` (and `serverDoc` when the
 * server returned one, e.g. on a document write conflict) for any non-OK
 * response, after the same stale-role 403 refresh-and-retry as azureGet.
 *
 * @param {string} path
 * @param {object} [body]
 * @param {{ keepalive?: boolean, silent?: boolean }} [options] - keepalive lets
 *   a final write survive a tab close / page unload; `silent: true` opts a
 *   background/best-effort call out of the generic error banner and
 *   auto-report — see surfaceApiError.
 * @returns {Promise<any>} the parsed JSON body
 */
export async function azurePost(path, body = {}, options = {}) {
  // See azureFetch above and tenantStateVersion.js — an endpoint that commits
  // the shared tenant_state row holds the debounced save for the duration of
  // the call and hands back the ROWVERSION it produced.
  const bumpsStateVersion = writesTenantState(path);
  const releaseStateVersion = bumpsStateVersion ? claimTenantStateVersion() : null;
  try {
    const doPost = async () => {
      /** @type {Record<string, string>} */
      const headers = { 'Content-Type': 'application/json' };
      if (getSessionToken()) headers['Authorization'] = `Bearer ${getSessionToken()}`;
      withStepUpHeader(headers);
      const payload = JSON.stringify(body);
      // keepalive lets a final write survive a tab close / page unload — the
      // browser guarantees delivery even after the page is gone. BUT the Fetch
      // spec (and every shipping browser) enforces a hard ~64 KiB quota across
      // ALL pending keepalive requests in a browsing context; a request that
      // would exceed it fails synchronously, before any network attempt at
      // all. A tenant's whole-state blob (documents, employees, ropaEntries,
      // and dozens of other collections still resident in the blob) routinely
      // exceeds that for any tenant carrying real data — which every tenant
      // does (see CLAUDE.md "Production Status"). Requesting keepalive on an
      // oversized body doesn't degrade delivery, it GUARANTEES the teardown
      // write silently never leaves the browser — the "queued retry never
      // lands" failure GH #5819 reproduced (ROPA: a network-loss mid-save
      // whose retry is still in flight when a real navigation follows falls
      // through to this exact teardown write). A plain (non-keepalive)
      // request has no delivery guarantee either, but it isn't quota-doomed
      // before it starts, so it is strictly the safer choice once the
      // payload can no longer honour the guarantee it was asked for.
      const keepaliveRequested = !!options.keepalive;
      const exceedsKeepaliveQuota =
        keepaliveRequested && new Blob([payload]).size > KEEPALIVE_BODY_BUDGET_BYTES;
      if (exceedsKeepaliveQuota) {
        console.warn(
          `[azureClient] Teardown write to ${path} exceeds the browser's keepalive quota ` +
            `(${KEEPALIVE_BODY_BUDGET_BYTES} bytes) — sending without keepalive; delivery is best-effort.`
        );
      }
      const send = () =>
        fetch(`${AZURE_FUNCTIONS_URL}${path}`, {
          method: 'POST',
          headers,
          body: payload,
          ...(keepaliveRequested && !exceedsKeepaliveQuota ? { keepalive: true } : {}),
        });
      // A teardown write cannot wait — the page is going away.
      return keepaliveRequested ? send() : fetchThroughRestart(send, { write: true });
    };

    let res = await retryAfterStepUp(await doPost(), doPost);
    if (res.status === 401 && getSessionToken()) {
      // Same fix as azureGet above (#5876/#5877/#5878) — throw the sentinel
      // surfaceApiError's session-expiry skip matches on, instead of falling
      // through to the generic `!res.ok` error below. httpStatus stays 401
      // (see azureGet's comment) so usePersistence.js's terminal-401 check
      // still fires and does not spin a doomed retry.
      handleSessionExpired();
      /** @type {ApiError} */
      const sessionExpiredError = new Error('Session expired');
      sessionExpiredError.httpStatus = 401;
      throw sessionExpiredError;
    }
    if (res.status === 403) {
      const errBody = await res.json().catch(() => ({}));
      if (errBody.error === 'Forbidden: admin role required') {
        const refresh = await refreshSessionToken();
        if (refresh.ok) {
          res = await doPost();
          if (res.status === 401) {
            handleSessionExpired();
            throw new Error('Session expired');
          }
          if (res.status === 403) {
            // F-55: still forbidden after a successful refresh — a genuine
            // permission denial, not a stale role. Surface the retried
            // response's message (falling back to the original) instead of
            // letting it fall through to a generic "returned 403".
            const retryBody = await res.json().catch(() => ({}));
            throw new Error(retryBody.error || errBody.error || 'Azure Function returned 403');
          }
        } else if (refresh.networkError) {
          // The refresh request itself failed to reach the server — this is a
          // transient network blip, not evidence the role is still stale. Don't
          // report it as a permission denial (F-55).
          throw new Error(
            'Could not verify permissions — network error during session refresh. Please try again.'
          );
        } else {
          throw new Error(errBody.error || 'Azure Function returned 403');
        }
      } else {
        // A policy refusal (e.g. employees-write's ERR_ON_BEHALF_SELF) names
        // its reason in `code`; keep the body so the screen can say which.
        // httpStatus is deliberately not set here: describeWriteError reads a
        // 403 status as "sign in again", which is not what this refusal means.
        /** @type {ApiError} */
        const forbidden = new Error(errBody.error || 'Azure Function returned 403');
        forbidden.responseBody = errBody;
        throw forbidden;
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      /** @type {ApiError} */
      const error = new Error(err.message || err.error || `Azure Function returned ${res.status}`);
      error.httpStatus = res.status;
      // A 409 body now carries a `reason` naming WHY the write was refused
      // (stale-version / seed-race / sql-deadlock / …) — see
      // azure-functions/shared/writeConflictDiag.js. Absent against a backend
      // deployed before that change, so every consumer must treat it as
      // optional.
      if (typeof err.reason === 'string') error.conflictReason = err.reason;
      // A 422 that refuses a payload against a structured rule set (a workflow
      // publish, PDWB-01) lists the rules it broke; carried as-is so the screen
      // can show the server's own reasons. Absent on every other response.
      if (Array.isArray(err.errors)) error.validationErrors = err.errors;
      // Set by azure-functions/shared/errorCapture.js on every 5xx. Its presence
      // means the SERVER already captured this failure — structured log line,
      // correlation id, deduplicated GitHub issue, with far more context than
      // the browser has. See surfaceApiError for why that changes what we do.
      if (typeof err.correlationId === 'string') error.correlationId = err.correlationId;
      if (err.serverDoc) error.serverDoc = err.serverDoc;
      // CONC-01: a refused state patch names each field that clashed.
      if (Array.isArray(err.conflicts)) error.conflicts = err.conflicts;
      // A refused versioned write can carry the state that won, so the caller
      // can resolve the conflict without a second round trip (GH #5358 —
      // state-write). Absent against a backend deployed before that change, so
      // callers must still treat it as optional.
      if (err.state !== undefined) {
        error.serverState = err.state;
        error.serverStateVersion = err.stateVersion ?? null;
      }
      // The refusal as the server wrote it, for endpoints whose 409 names more
      // than this transport lifts out — risk-ai-assess's FIELD_CONFLICT carries
      // { field, yourValue, existingValue, by, at } and RISK_BEING_CREATED the
      // risk id, which the screen shows as-is (RAI-05, TRYGGR_INTENT §13 d).
      error.responseBody = err;
      throw error;
    }
    const data = await res.json();
    if (bumpsStateVersion) adoptTenantStateVersion(data);
    return data;
  } catch (error) {
    surfaceApiError(error, { path, method: 'POST', silent: options.silent });
    throw error;
  } finally {
    if (releaseStateVersion) releaseStateVersion();
  }
}

/**
 * Get the Azure Functions base URL (for Edge Function replacements in tenantApi).
 * @returns {string}
 */
export function getAzureFunctionsUrl() {
  return AZURE_FUNCTIONS_URL;
}

// ── Environment-aware access (super-admin cross-environment management) ─────
//
// The production super-admin portal needs to call Azure Functions in all
// three environments (production, test, development). Each environment has
// its own Function App with a different base URL. This is the one
// legitimate delta from the single-environment calls above — everything
// else (fetch, 401 handling, error parsing) is shared.
//
// Env vars:
//   VITE_AZURE_FUNCTIONS_URL      — current environment (always set)
//   VITE_AZURE_FUNCTIONS_URL_TEST — test Function App (set in production build)
//   VITE_AZURE_FUNCTIONS_URL_DEV  — dev Function App (set in production build)

/** @type {Record<AzureEnvironment, string>} */
const ENV_URLS = {
  production: AZURE_FUNCTIONS_URL,
  test: import.meta.env.VITE_AZURE_FUNCTIONS_URL_TEST || AZURE_FUNCTIONS_URL,
  development: import.meta.env.VITE_AZURE_FUNCTIONS_URL_DEV || AZURE_FUNCTIONS_URL,
};

/**
 * Get the Azure Functions base URL for a given environment.
 * @param {AzureEnvironment} env
 * @returns {string}
 */
export function getEnvFunctionsUrl(env) {
  return ENV_URLS[env] || ENV_URLS.production;
}

/**
 * Resolve the base URL for a non-'production' env call, refusing to proceed
 * if that env has no distinct URL configured and would otherwise silently
 * fall back to the production Function App (e.g. a super-admin "Test" DELETE
 * landing on prod because VITE_AZURE_FUNCTIONS_URL_TEST was never set).
 *
 * @param {AzureEnvironment} env
 * @returns {string}
 */
function resolveEnvUrlOrThrow(env) {
  const baseUrl = getEnvFunctionsUrl(env);
  if (env !== 'production' && baseUrl === ENV_URLS.production) {
    throw new Error(
      `[azureClient] Refusing to call the "${env}" environment — its Function App URL is not configured and would fall back to production.`
    );
  }
  return baseUrl;
}

/**
 * Check if a non-current environment is properly configured.
 * @param {AzureEnvironment} env
 * @returns {boolean}
 */
export function isEnvConfigured(env) {
  return !!ENV_URLS[env] && ENV_URLS[env] !== ENV_URLS.production;
}

/** @type {Record<AzureEnvironment, { label: string, registryId: string, color: string }>} */
export const ENVIRONMENTS = {
  production: { label: 'Production', registryId: 'registry', color: '#2dd4bf' },
  test: { label: 'Test', registryId: 'registry-test', color: '#fbbf24' },
  development: { label: 'Development', registryId: 'registry-development', color: '#fb7185' },
};

/**
 * Returns true if at least one non-production environment is configured
 * with a distinct Function App URL.
 * @returns {boolean}
 */
export function isDevTestAvailable() {
  return isEnvConfigured('test') || isEnvConfigured('development');
}

const ENV_DEFAULT_TIMEOUT_MS = 30000;

/**
 * Shared fetch wrapper for env-scoped calls: applies a request timeout (via
 * AbortController) and unified 401/error-body handling so azureEnvGet/Post/
 * Delete don't each reimplement it — and don't each silently omit it.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {{ path?: string, method?: string }} [errorContext]
 * @returns {Promise<any>} the parsed JSON body
 */
async function envFetch(url, options = {}, errorContext = {}) {
  // §16: the super-admin portal manages every environment through these calls,
  // so they carry the step-up token and re-verify once on a step-up refusal
  // exactly as azureGet/azurePost do (FDNA-02b). Each attempt has its own
  // timeout, so time spent in the re-verify dialog never counts against it.
  const send = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ENV_DEFAULT_TIMEOUT_MS);
    try {
      return await fetch(url, {
        ...options,
        headers: withStepUpHeader({
          .../** @type {Record<string, string>} */ (options.headers || {}),
        }),
        signal: controller.signal,
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('Azure Function request timed out');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    const res = await retryAfterStepUp(await send(), send);

    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Session expired');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      /** @type {ApiError} */
      const failure = new Error(err.error || `Azure Function returned ${res.status}`);
      // Keep what the server said, not only the message: a caller routing a
      // 409/503 to the right screen (the Releases tab's conflict and
      // records-held refusals) needs the status and the body's field/code.
      failure.httpStatus = res.status;
      // Same lifts as azurePost, so a 409 here is classified (expected vs
      // defect) by surfaceApiError exactly as it is on the same-origin path.
      if (typeof err.reason === 'string') failure.conflictReason = err.reason;
      if (typeof err.correlationId === 'string') failure.correlationId = err.correlationId;
      failure.responseBody = err;
      throw failure;
    }
    return await res.json();
  } catch (error) {
    surfaceApiError(error, errorContext);
    throw error;
  }
}

/**
 * GET request to an Azure Function in a specific environment.
 * @param {AzureEnvironment} env
 * @param {string} path
 * @param {QueryParams} [queryParams]
 * @returns {Promise<any>}
 */
export async function azureEnvGet(env, path, queryParams = {}) {
  const baseUrl = resolveEnvUrlOrThrow(env);
  const url = new URL(`${baseUrl}${path}`);
  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  /** @type {Record<string, string>} */

  const headers = { 'Content-Type': 'application/json' };
  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return envFetch(url.toString(), { headers }, { path, method: 'GET' });
}

/**
 * POST request to an Azure Function in a specific environment.
 * @param {AzureEnvironment} env
 * @param {string} path
 * @param {object} [body]
 * @returns {Promise<any>}
 */
export async function azureEnvPost(env, path, body = {}) {
  const baseUrl = resolveEnvUrlOrThrow(env);
  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json' };
  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return envFetch(
    `${baseUrl}${path}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
    { path, method: 'POST' }
  );
}

/**
 * DELETE request to an Azure Function in a specific environment.
 * @param {AzureEnvironment} env
 * @param {string} path
 * @param {QueryParams} [queryParams]
 * @returns {Promise<any>}
 */
export async function azureEnvDelete(env, path, queryParams = {}) {
  const baseUrl = resolveEnvUrlOrThrow(env);
  const url = new URL(`${baseUrl}${path}`);
  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  /** @type {Record<string, string>} */

  const headers = { 'Content-Type': 'application/json' };
  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return envFetch(url.toString(), { method: 'DELETE', headers }, { path, method: 'DELETE' });
}
